import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssignmentReviewContextDetails } from '@app/app/page-modules/course/models/course-assignments.model';
import { CardWithProgress } from '@app/app/page-modules/resource/models';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import {
  CardType,
  ConnectCardBadgeRequest,
  ConnectedBadgeDetails,
  ConnectedBadgeStatus,
  EditorContent,
  Resource,
} from '@app/app/shared/models';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  CARDS_DATA_SERVICE,
  CardSectionUpdateRequest,
  CardsDataService,
  CreateIndependentCardRequest,
  CreatedAssessmentInfo,
  CreatedCardInfo,
  CreatedEventInfo,
} from './cards-data.service';

/** The human-readable `ErrorMessage.message` a 4xx body carries, when it has one. */
function serverMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  const message = (error.error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim().length > 0 ? message : null;
}

/** Card endpoints live on the resource microservice; respect the local override if set. */
const CARDS_API_ROOT = Location.joinWithSlash(environment.localServiceOverrides?.resourcesBaseUrl || environment.apiRootUrl || '', '');

@Injectable()
export class ApiCardsDataService implements CardsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  createEmptyCard(playlistUid: string, sectionUid: string): ObservableResult<CreatedCardInfo> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `playlists/${playlistUid}/sections/${sectionUid}?cardType=RESOURCE`);

    return this.client.post<Resource>(url, null, {}, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() }).pipe(
      switchMap(({ body }) =>
        ObservableResult.ofSuccess({
          cardUid: body._id,
          cardUri: body.settings?.publication?.uri || body._id,
          sectionUid: body.content?.sections?.[0]?.uid,
        } as CreatedCardInfo),
      ),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateResource'))),
    );
  }

  createEmptyEvent(playlistUid: string, sectionUid: string): ObservableResult<CreatedEventInfo> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `playlists/${playlistUid}/sections/${sectionUid}?cardType=EVENT`);

    return this.client.post<Resource>(url, null, {}, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() }).pipe(
      switchMap(({ body }) =>
        ObservableResult.ofSuccess({
          eventUid: body._id,
          eventUri: body.settings?.publication?.uri || body._id,
        } as CreatedEventInfo),
      ),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateEventCard'))),
    );
  }

  createEmptyAssessment(playlistUid: string, sectionUid: string, is360: boolean): ObservableResult<CreatedAssessmentInfo> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `playlists/${playlistUid}/sections/${sectionUid}`);

    // As in v2: the flag is only sent when it is on, never as `is360=false`.
    const params: { [name: string]: string | boolean } = { cardType: 'ASSESSMENT' };
    if (is360) {
      params.is360 = true;
    }

    return this.client.post<Resource>(url, null, params, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() }).pipe(
      switchMap(({ body }) =>
        ObservableResult.ofSuccess({
          assessmentUid: body._id,
          assessmentUri: body.settings?.publication?.uri || body._id,
        } as CreatedAssessmentInfo),
      ),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateAssessment'))),
    );
  }

  createIndependentCard(type: CardType, request?: CreateIndependentCardRequest) {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards`);

    const queryParams = {
      type: type,
    };

    const requestBody = request ?? {};

    return this.client
      .post<Resource>(url, requestBody, queryParams, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateCard'))),
      );
  }

  getCard(cardUri: string, playlistPath?: string, includeProgress?: boolean): ObservableResult<Resource> {
    const endpoint = playlistPath
      ? `cards/v3/${cardUri}?in=${encodeURIComponent(playlistPath)}${includeProgress ? '&includeProgress=true' : ''}`
      : `cards/${cardUri}`;
    const url = Location.joinWithSlash(CARDS_API_ROOT, endpoint);
    const languageCode = LanguageCodeHelper.getExplicitContentLanguageCode();
    const headers = languageCode ? { 'Accept-Language': languageCode } : undefined;

    return this.client.get<Resource | CardWithProgress>(url, undefined, headers).pipe(
      switchMap(({ body }) => {
        // Both endpoints wrap the card when progress rides along: `{ card, progress }`.
        const wrapped = body as CardWithProgress;
        if (wrapped?.card) {
          const card = wrapped.card as Resource;
          return ObservableResult.ofSuccess(
            includeProgress && wrapped.progress ? ({ ...card, progress: wrapped.progress } as Resource) : card,
          );
        }
        return ObservableResult.ofSuccess(body as Resource);
      }),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetResource'))),
    );
  }

  getCardById(cardId: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/by-id/${cardId}`);
    const languageCode = LanguageCodeHelper.getExplicitContentLanguageCode();
    const headers = languageCode ? { 'Accept-Language': languageCode } : undefined;

    return this.client.get<CardWithProgress>(url, undefined, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body.card)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetResource'))),
    );
  }

  getCardAssignmentReview(cardId: string): Observable<AssignmentReviewContextDetails[]> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardId}/assignment-review`);

    return this.client.get<AssignmentReviewContextDetails[]>(url).pipe(map(({ body }) => body));
  }

  updateCardSection(cardUid: string, sectionUid: string, request: CardSectionUpdateRequest): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/sections/${sectionUid}`);
    const languageCode = request.languageCode || LanguageCodeHelper.getExplicitContentLanguageCode();
    const payload = {
      uid: request.uid,
      cardHeadline: request.cardHeadline,
      dynamicContent: request.dynamicContent as EditorContent[],
      languageCode: languageCode || undefined,
    };
    const headers = languageCode ? { 'Content-Language': languageCode } : undefined;

    return this.client.put<void>(url, payload, undefined, headers).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      // Keep the server's own wording when it has some. A card backing a submitted assignment task is locked,
      // and only the rejection says so — the client has no way to know a card is a task source, so replacing
      // the message with the generic "couldn't update" would leave the admin with no idea why saving failed.
      catchError((error) =>
        ObservableResult.ofError(serverMessage(error) ?? this.translocoService.translate('translations.errors.errorUpdateResource')),
      ),
    );
  }

  updateCardHeadline(cardUid: string, headline: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}`);
    const payload = { headline };

    return this.client.patch<void>(url, payload).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateResource'))),
    );
  }

  connectCardBadge(cardUid: string, request: ConnectCardBadgeRequest): ObservableResult<ConnectedBadgeDetails> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/badges`);

    return this.client
      .put<ConnectedBadgeDetails>(url, request, undefined, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => this.badgeError(err, 'translations.errors.errorConnectCardBadge')),
      );
  }

  updateCardBadgeStatus(cardUid: string, status: ConnectedBadgeStatus): ObservableResult<ConnectedBadgeDetails> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/badges`);

    return this.client
      .patch<ConnectedBadgeDetails>(url, { status }, undefined, { 'Content-Language': LanguageCodeHelper.getContentLanguageCode() })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => this.badgeError(err, 'translations.errors.errorUpdateBadgeStatus')),
      );
  }

  disconnectCardBadge(cardUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/badges`);

    return this.client.delete<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err) => this.badgeError(err, 'translations.errors.errorDisconnectBadge')),
    );
  }

  /**
   * The badge endpoints answer with a `{ message }` body explaining the rejection (wrong organization,
   * assignment card, missing connection). Surface it when present — the generic fallback cannot say
   * which of those happened.
   */
  private badgeError(err: HttpErrorResponse, fallbackKey: string) {
    const message = err?.error?.message;
    return ObservableResult.ofError(typeof message === 'string' && message ? message : this.translocoService.translate(fallbackKey));
  }

  archiveCard(cardUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/publishing/archive`);

    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorArchiveCard'))),
    );
  }

  discardCard(cardUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/discard`);

    return this.client.delete<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDiscardCard'))),
    );
  }

  publishCard(cardUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/publishing/publish`);

    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorPublishCard'))),
    );
  }

  unpublishCard(cardUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(CARDS_API_ROOT, `cards/${cardUid}/settings/publishing/unpublish`);

    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnPublishCard'))),
    );
  }
}

export const CARDS_API_DATA_SERVICE_PROVIDER = {
  provide: CARDS_DATA_SERVICE,
  useClass: ApiCardsDataService,
};
